import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import { useSearchParams } from 'react-router-dom'
import { message, Tooltip } from 'antd'
import { motion } from 'framer-motion'
import { useMediaQuery } from 'hooks/useMediaQuery'
import { Building, Floor } from 'models/Building'
import { useRecoilState, useRecoilValue } from 'recoil'
import { listPointState } from 'store/buildingStore'
import currentViewerState from 'store/viewerStore'

import LazyThumbnail, { ImageType } from 'components/atoms/LazyThumbnail'
import BusinessSettingIcon from 'components/icons/BusinessSettingIcon'
import ChevronDownIcon from 'components/icons/ChevronDownIcon'
import SettingLocation from 'components/icons/SettingLocation'
import ShowSubImageArrown from 'components/icons/ShowSubImageArrown'
import ZoomIcon from 'components/icons/ZoomIcon'
import ZoomIconOut from 'components/icons/ZoomIconOut'

/* eslint-disable react/prop-types, react/require-default-props */
import useDragScroll from '../../hook/Carousel/useDragScroll'
import useThumbnailCarousel from '../../hook/Carousel/useThumbnailCarousel'
import { SubImageOverlay } from '../type'

import SubImageThubnailCarousel from './SubImageThubnailCarousel'

import './thumbnailCarousel.css'

// Extend TypeScript interfaces for fullscreen APIs
declare global {
  interface HTMLElement {
    webkitRequestFullscreen?: () => Promise<void>
    msRequestFullscreen?: () => Promise<void>
    webkitEnterFullscreen?: () => Promise<void>
    mozRequestFullScreen?: () => Promise<void>
  }

  interface Document {
    webkitExitFullscreen?: () => Promise<void>
    msExitFullscreen?: () => Promise<void>
    mozCancelFullScreen?: () => Promise<void>
    webkitFullscreenElement?: Element | null
    msFullscreenElement?: Element | null
    mozFullScreenElement?: Element | null
    fullscreenElement?: Element | null
    webkitIsFullScreen?: boolean
    mozFullScreen?: boolean
    msFullscreenEnabled?: boolean
    webkitFullscreenEnabled?: boolean
    mozFullScreenEnabled?: boolean
  }
}

interface ThumbnailCarouselProps {
  setOpenBusinessInfo: Dispatch<SetStateAction<boolean>>
  setOpenMapLocation: Dispatch<SetStateAction<boolean>>
  setSelectedPointID: Dispatch<SetStateAction<string>>
  building: Building | undefined
  floors: Floor[] | undefined
  onThumbnailsLoaded?: (floorId?: string) => void
}
/* eslint-disable react/prop-types */
function ThumbnailCarousel({
  setSelectedPointID,
  building,
  floors,
  setOpenBusinessInfo,
  setOpenMapLocation,
  onThumbnailsLoaded = () => {}
}: ThumbnailCarouselProps) {
  const points = useRecoilValue(listPointState)
  const [currentViewer, setCurrentViewer] = useRecoilState(currentViewerState)
  const thumbnailRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const matches = useMediaQuery('(min-width: 768px)')
  const [searchParams] = useSearchParams()
  // Track whether auto-open from subimage param has been triggered
  const subimageAutoOpenedRef = useRef(false)
  // Removed pendingAutoOpenPointId as we now rely on the stable stability-check effect
  // Removed tooltip state for title area — we render plain title blocks and
  // handle dragging on the parent container.
  // state of sub-image overlay
  const [subImageOverlay, setSubImageOverlay] = useState<SubImageOverlay>({
    isOpen: false,
    pointId: '',
    top: 0,
    left: 0,
    width: 0
  })
  const [isZoom, setIsZoom] = useState(false)

  // Track auto-opening to prevent re-opening if user manually closes it
  const autoOpenedForImageRef = useRef<string | number | null>(null)

  const currentFloorName =
    floors?.find((f) => f.id === currentViewer.selectedFloor)?.name || ''

  const currentImageName = (() => {
    const { pointDetail } = currentViewer
    if (!pointDetail) {
      return ''
    }
    if (currentViewer.selectedImage === pointDetail.image360?.id) {
      return pointDetail.image360?.name || ''
    }
    return (
      pointDetail.subImages?.find((s) => s.id === currentViewer.selectedImage)
        ?.name || ''
    )
  })()

  const { isOpen, setIsOpen, handleThumbnailClick, handleSelectSubImage } =
    useThumbnailCarousel({
      setSelectedPointID,
      building
    })

  const mainCarousel = useDragScroll(2)
  const subImageCarousel = useDragScroll(3, subImageOverlay.isOpen)
  const [thumbnailsLoadedCount, setThumbnailsLoadedCount] = useState(0)

  const toggleButtonRef = useRef<HTMLDivElement | null>(null)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const rafRef = useRef<number | null>(null)
  // Suppress ResizeObserver loop errors - this is a known browser warning
  // that doesn't affect functionality
  useEffect(() => {
    const handleResizeObserverError = (e: ErrorEvent) => {
      if (e.message && e.message.includes('ResizeObserver loop')) {
        e.stopImmediatePropagation()
        return false
      }
    }

    window.addEventListener('error', handleResizeObserverError)
    return () => {
      window.removeEventListener('error', handleResizeObserverError)
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [])

  // Reset thumbnails count if points change
  useEffect(() => {
    setThumbnailsLoadedCount(0)
  }, [points.length])

  useEffect(() => {
    const handleMouseMoveCleanup = (e: MouseEvent) => {
      try {
        if (!document.body) return
        if (
          document.body.classList.contains('krpano-dragging') &&
          // no mouse buttons pressed
          (e as any).buttons === 0
        ) {
          document.body.classList.remove('krpano-dragging')
          document.body.style.cursor = ''
          document.body.style.removeProperty('--krpano-cursor')
          // rely on body-level cursor/class — avoid per-element inline style writes
        }
      } catch (err) {
        // ignore
      }
    }

    document.addEventListener('mousemove', handleMouseMoveCleanup)
    return () =>
      document.removeEventListener('mousemove', handleMouseMoveCleanup)
  }, [])

  // Preload thumbnails (regardless of IntersectionObserver) and increment counter
  useEffect(() => {
    let canceled = false
    const toLoad: string[] = points
      .map((p) =>
        p.image360?.multires
          ? `${p.image360.tilesUrl}thumb.jpg`
          : p.image360?.thumbnail
      )
      .filter(Boolean) as string[]

    if (toLoad.length === 0) return

    toLoad.forEach((src) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.src = src
      if (img.complete) {
        if (!canceled) setThumbnailsLoadedCount((c) => c + 1)
        return
      }
      img.onload = () => {
        if (!canceled) setThumbnailsLoadedCount((c) => c + 1)
      }
      img.onerror = () => {
        if (!canceled) setThumbnailsLoadedCount((c) => c + 1)
      }
    })

    return () => {
      canceled = true
    }
  }, [points])

  // When thumbnails are all loaded, notify parent
  useEffect(() => {
    const hasThumb = (p: any) =>
      !!(p.image360 && (p.image360.multires || p.image360.thumbnail))
    const requiredThumbCount = points.filter(hasThumb).length
    // FIX: Also notify when there are no thumbnails to load
    if (
      requiredThumbCount === 0 ||
      thumbnailsLoadedCount >= requiredThumbCount
    ) {
      onThumbnailsLoaded?.()
    }
  }, [points, thumbnailsLoadedCount, onThumbnailsLoaded])
  const handleToggleThumbnail = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      e.nativeEvent.stopImmediatePropagation()
      setIsOpen((prev) => !prev)
    },
    [setIsOpen]
  )

  const handleToggleMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()
  }, [])

  // Stop pointer-down from starting krpano drag on action buttons
  const stopPointerDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      try {
        e.stopPropagation()
        const ne: any = (e as any).nativeEvent
        if (ne && typeof ne.stopImmediatePropagation === 'function') {
          ne.stopImmediatePropagation()
        }
      } catch (err) {
        // ignore
      }
    },
    []
  )

  const startKrpanoDrag = useCallback(
    (
      startX: number,
      startY: number,
      pointerType: 'mouse' | 'touch',
      tooltipOpen: boolean
    ) => {
      if (tooltipOpen) return

      const krpano =
        (document.getElementById('embedpano-viewer') as any) ||
        (document.getElementById('viewer-pano-container') as any)
      if (!krpano) return

      // get initial h and v
      const initialH = Number(krpano.get?.('view.hlookat')) || 0
      const initialV = Number(krpano.get?.('view.vlookat')) || 0
      const sensitivityH = 0.15 // degrees per pixel for horizontal
      const sensitivityV = 0.15 // degrees per pixel for vertical

      const onMove = (ev: any) => {
        if (ev.touches && ev.touches.length) {
          ev.preventDefault()
        }
        // Cancel any pending RAF to avoid multiple updates per frame
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current)
        }

        rafRef.current = requestAnimationFrame(() => {
          let x = 0
          let y = 0
          if (ev.touches && ev.touches.length) {
            x = ev.touches[0].clientX
            y = ev.touches[0].clientY
          } else {
            x = ev.clientX
            y = ev.clientY
          }
          const deltaX = x - startX
          const deltaY = y - startY
          const newH = initialH - deltaX * sensitivityH
          // Flip vertical sign so moving pointer up/down matches expected pitch
          const newV = initialV - deltaY * sensitivityV
          try {
            // prefer set if available
            if (krpano.set) {
              krpano.set('view.hlookat', newH)
              krpano.set('view.vlookat', newV)
            } else if (krpano.call) {
              krpano.call(`lookto(${newH}, ${newV}, 0)`)
            }
          } catch (e) {
            // ignore
          }
        })
      }

      const onUp = () => {
        // Clean up any pending RAF
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }

        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
        document.removeEventListener('touchmove', onMove)
        document.removeEventListener('touchend', onUp)
        // reset cursor and any drag class/variable we set
        if (document.body) {
          document.body.style.cursor = ''
          document.body.classList.remove('krpano-dragging')
          document.body.style.removeProperty('--krpano-cursor')
          // rely on body-level cursor/class — avoid per-element inline style writes
        }
      }

      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
      document.addEventListener('touchmove', onMove, { passive: false })
      document.addEventListener('touchend', onUp)
    },
    []
  )

  const handleLeftMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      startKrpanoDrag(e.clientX, e.clientY, 'mouse', false)
      // set cursor to whatever the viewer uses and force it across children
      try {
        const viewerEl =
          (document.getElementById('embedpano-viewer') as HTMLElement | null) ||
          (document.getElementById(
            'viewer-pano-container'
          ) as HTMLElement | null)
        let viewerCursor = viewerEl
          ? getComputedStyle(viewerEl).cursor || 'auto'
          : 'auto'
        // If the viewer doesn't expose a helpful cursor (auto/default),
        // use a visible dragging cursor so title area matches non-title area.
        if (
          !viewerCursor ||
          viewerCursor === 'auto' ||
          viewerCursor === 'default'
        ) {
          viewerCursor = 'move'
        }
        if (document.body) {
          document.body.style.setProperty('--krpano-cursor', viewerCursor)
          document.body.style.cursor = viewerCursor
          // rely on body-level cursor/class — avoid per-element inline style writes
        }
      } catch (e) {
        // ignore
      }
    },
    [startKrpanoDrag]
  )

  const handleLeftTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const t = e.touches && e.touches[0]
      if (!t) return
      e.preventDefault()
      e.stopPropagation()
      startKrpanoDrag(t.clientX, t.clientY, 'touch', false)
      // set cursor to whatever the viewer uses and force it across children
      try {
        const viewerEl =
          (document.getElementById('embedpano-viewer') as HTMLElement | null) ||
          (document.getElementById(
            'viewer-pano-container'
          ) as HTMLElement | null)
        let viewerCursor = viewerEl
          ? getComputedStyle(viewerEl).cursor || 'auto'
          : 'auto'
        if (
          !viewerCursor ||
          viewerCursor === 'auto' ||
          viewerCursor === 'default'
        ) {
          viewerCursor = 'move'
        }
        if (document.body) {
          document.body.style.setProperty('--krpano-cursor', viewerCursor)
          document.body.classList.add('krpano-dragging')
          document.body.style.cursor = viewerCursor
          // rely on body-level cursor/class — avoid per-element inline style writes
        }
      } catch (e) {
        // ignore
      }
    },
    [startKrpanoDrag]
  )

  const handleActionBarMouseEnter = useCallback(() => {
    // keep default cursor on hover; cursor will switch when user starts dragging
  }, [])

  const handleActionBarMouseLeave = useCallback(() => {
    // only reset body cursor when we're NOT forcing the krpano drag cursor
    if (!document.body?.classList.contains('krpano-dragging')) {
      document.body.style.cursor = ''
    }
  }, [])

  const handleActionBarClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
  }, [])

  const handleToggleSubImages = (pointId: string) => {
    // If open and click same point, close
    if (subImageOverlay.isOpen && subImageOverlay.pointId === pointId) {
      setSubImageOverlay({
        isOpen: false,
        pointId: '',
        top: 0,
        left: 0,
        width: 0
      })
      return
    }

    // If open other point, close old point and open new point
    if (subImageOverlay.isOpen) {
      setSubImageOverlay({
        isOpen: false,
        pointId: '',
        top: 0,
        left: 0,
        width: 0
      })
      // Wait a bit then open point new
      setTimeout(() => {
        const el = thumbnailRefs.current[pointId]
        if (el) {
          const rect = el.getBoundingClientRect()
          setSubImageOverlay({
            isOpen: true,
            pointId,
            top: rect.top,
            left: rect.left - 5,
            width: rect.width
          })
        }
      }, 100)
      return
    }

    // Get position of thumbnail on screen
    const el = thumbnailRefs.current[pointId]
    if (el) {
      const rect = el.getBoundingClientRect()
      setSubImageOverlay({
        isOpen: true,
        pointId,
        top: rect.top,
        left: rect.left - 5,
        width: rect.width
      })
    }
  }

  // Mousedown outside listener to close SubImage Carousel
  useEffect(() => {
    if (!subImageOverlay.isOpen) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node

      const overlay = document.getElementById('subimage-overlay')
      if (overlay && overlay.contains(target)) {
        return
      }

      // Ignore if clicking the toggle button itself, let the onClick handle it
      if ((target as Element).closest?.('.subimage-toggle-btn')) {
        return
      }
      const panoContainer = document.getElementById('viewer-pano-container')
      const embedpanoViewer = document.getElementById('embedpano-viewer')
      if (
        (panoContainer && panoContainer.contains(target)) ||
        (embedpanoViewer && embedpanoViewer.contains(target))
      ) {
        return
      }

      // Keep sub-images open while interacting with annotation modal
      const annotationModalRoot = document.querySelector(
        '.annotation-detail-modal'
      )
      if (
        annotationModalRoot &&
        (annotationModalRoot === target || annotationModalRoot.contains(target))
      ) {
        return
      }

      const thumbnailCarousel = document.querySelector(
        '.tour-view-page-carousel'
      )
      // Don't close if clicking on toggle button or anywhere in carousel
      if (thumbnailCarousel && thumbnailCarousel.contains(target)) {
        // But allow closing if clicking outside the action bar (where toggle button is)
        const actionBar = thumbnailCarousel.querySelector(
          '.flex.justify-between.items-center'
        )
        if (actionBar && actionBar.contains(target)) {
          return
        }
      }

      setSubImageOverlay({
        isOpen: false,
        pointId: '',
        top: 0,
        left: 0,
        width: 0
      })
    }
    // Use capture phase to ensure we can stop propagation early
    document.addEventListener('mousedown', handleClick, true)
    return () => document.removeEventListener('mousedown', handleClick, true)
  }, [subImageOverlay.isOpen])

  useEffect(() => {
    if (
      !subImageOverlay.isOpen ||
      !currentViewer.selectedPoint ||
      currentViewer.selectedPoint === subImageOverlay.pointId
    ) {
      return
    }

    setSubImageOverlay({
      isOpen: false,
      pointId: '',
      top: 0,
      left: 0,
      width: 0
    })
  }, [
    currentViewer.selectedPoint,
    subImageOverlay.isOpen,
    subImageOverlay.pointId
  ])

  const enterFullscreen = useCallback(async () => {
    const element = document.documentElement
    try {
      if (element.requestFullscreen) {
        await element.requestFullscreen()
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen()
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen()
      } else if (element.webkitEnterFullscreen) {
        await element.webkitEnterFullscreen()
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen()
      } else {
        message.warning('このブラウザは全画面表示をサポートしていません')
      }
    } catch (err) {
      message.error('全画面表示に切り替えられませんでした')
    }
  }, [])

  const exitFullscreen = useCallback(async () => {
    try {
      if (document.exitFullscreen) {
        await document.exitFullscreen()
      } else if (document.webkitExitFullscreen) {
        await document.webkitExitFullscreen()
      } else if (document.msExitFullscreen) {
        await document.msExitFullscreen()
      } else if (document.mozCancelFullScreen) {
        await document.mozCancelFullScreen()
      }
    } catch (err) {
      // Silent error
    }
  }, [])

  const isFullscreen = useCallback(() => {
    return !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    )
  }, [])

  const toggleFullScreen = useCallback(async () => {
    try {
      if (isFullscreen()) {
        await exitFullscreen()
      } else {
        await enterFullscreen()
      }
    } catch (error) {
      message.error('全画面表示の切り替え中にエラーが発生しました')
    }
  }, [enterFullscreen, exitFullscreen, isFullscreen])

  useEffect(() => {
    const activePointId = currentViewer.selectedPoint

    if (activePointId && mainCarousel.containerRef.current) {
      const selectedElement = mainCarousel.containerRef.current.querySelector(
        `[data-point-id="${activePointId}"]`
      ) as HTMLElement

      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center'
        })
      }
    }
  }, [mainCarousel.containerRef, currentViewer.selectedPoint, isOpen, points])
  // Effect 1: detect subimage URL param and open the carousel drawer.
  // The actual sub-image menu opening is handled by the stable stability-check effect below.

  useEffect(() => {
    const urlSubimage = searchParams.get('subimage')
    if (!urlSubimage || subimageAutoOpenedRef.current) return

    const pointId = currentViewer.selectedPoint
    if (!pointId) return

    const activePoint = points.find((p) => p.id === pointId)
    if (!activePoint?.isSubImages) return

    subimageAutoOpenedRef.current = true
    setIsOpen(true)
    // No longer setting pendingAutoOpenPointId to avoid premature opening with wrong coordinates
  }, [searchParams, currentViewer.selectedPoint, points, setIsOpen])

  useEffect(() => {
    if (
      currentViewer.selectedPoint &&
      currentViewer.selectedImage === currentViewer.pointDetail?.image360?.id
    ) {
      handleThumbnailClick(currentViewer.selectedPoint)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewer.selectedImage])

  useEffect(() => {
    const handleChange = () => {
      setIsZoom(isFullscreen())
    }

    document.addEventListener('fullscreenchange', handleChange)

    const scrollTimeout = scrollTimeoutRef.current
    return () => {
      document.removeEventListener('fullscreenchange', handleChange)
      // Cleanup scroll timeout on unmounting
      if (scrollTimeout) {
        clearTimeout(scrollTimeout)
      }
    }
  }, [isFullscreen])

  // Auto-open subimage menu if subimage is selected (e.g., from URL)
  useEffect(() => {
    const { selectedPoint, selectedImage, pointDetail } = currentViewer

    if (
      selectedPoint &&
      selectedImage &&
      pointDetail &&
      pointDetail.id === selectedPoint &&
      !subImageOverlay.isOpen &&
      isOpen &&
      autoOpenedForImageRef.current !== selectedImage
    ) {
      const isSubImage = pointDetail.subImages?.some(
        (sub) => sub.id === selectedImage
      )

      if (isSubImage) {
        let retryCount = 0
        let prevLeft = -9999
        let stableCount = 0
        let innerTimeoutId: NodeJS.Timeout | null = null

        const tryOpen = () => {
          const el = thumbnailRefs.current[selectedPoint]
          const container = mainCarousel.containerRef.current

          if (el && container) {
            const elRect = el.getBoundingClientRect()
            const containerRect = container.getBoundingClientRect()

            const isVisible =
              elRect.right >= containerRect.left &&
              elRect.left <= containerRect.right

            const isCurrentlyStable =
              prevLeft !== -9999 && Math.abs(elRect.left - prevLeft) < 2

            if (isCurrentlyStable) {
              stableCount += 1
            } else {
              stableCount = 0
            }

            // Wait until the element hasn't moved for 3 consecutive ticks (300ms)
            // to ensure smooth scroll has properly finished at the destination.
            const isTrulyStable = stableCount >= 3

            if (!isVisible || !isTrulyStable) {
              prevLeft = elRect.left
              if (retryCount < 100) {
                retryCount += 1
                innerTimeoutId = setTimeout(tryOpen, 100)
              }
              return
            }

            handleToggleSubImages(selectedPoint)
            autoOpenedForImageRef.current = selectedImage
          } else if (retryCount < 100) {
            retryCount += 1
            innerTimeoutId = setTimeout(tryOpen, 100)
          }
        }

        const timeoutId = setTimeout(tryOpen, 100)

        return () => {
          clearTimeout(timeoutId)
          if (innerTimeoutId) clearTimeout(innerTimeoutId)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentViewer.selectedPoint,
    currentViewer.selectedImage,
    currentViewer.pointDetail,
    isOpen,
    subImageOverlay.isOpen,
    points
  ])

  return (
    <div className="w-screen tour-view-page-carousel relative">
      {/* Action Bar - no background */}
      <div
        className="relative flex justify-between items-center w-[98%] mx-auto py-3"
        onClick={handleActionBarClick}
        onMouseDown={handleLeftMouseDown}
        onTouchStart={handleLeftTouchStart}
        onMouseEnter={handleActionBarMouseEnter}
        onMouseLeave={handleActionBarMouseLeave}
      >
        <div
          className={`flex gap-x-7 items-center ${
            !matches ? 'w-[70%]' : 'w-full'
          }`}
        >
          <div
            ref={toggleButtonRef}
            onClick={handleToggleThumbnail}
            onMouseDown={handleToggleMouseDown}
            className=" w-[45px] h-[24px] flex items-center justify-center rounded-md border border-gray-300 transition active:opacity-80"
          >
            <div className="relative w-5 h-5 ">
              <ChevronDownIcon
                className={`absolute transition-all duration-300 ease-in-out transform ${
                  !isOpen ? 'rotate-0' : '-rotate-180'
                }
                ${!matches ? 'scale-75' : 'scale-100'}`}
              />
            </div>
          </div>

          <div
            className="flex flex-col ml-2 text-white min-w-0"
            style={{
              width: matches ? '70%' : '100%',
              // allow the krpano drag cursor (set on document.body as --krpano-cursor)
              // to take effect here when dragging; fall back to 'auto' otherwise
              cursor: 'var(--krpano-cursor, auto)'
            }}
          >
            {/* Floor Name (no tooltip) */}
            <div
              className="text-ellipsis overflow-hidden"
              style={{
                fontSize: matches ? '14px' : '12px',
                fontWeight: 'bold',
                lineHeight: '1.4',
                maxHeight: '2.8em',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                display: '-webkit-box',
                wordBreak: 'break-word',
                pointerEvents: 'auto'
              }}
            >
              {currentFloorName ? (
                <span
                  className="block overflow-hidden text-ellipsis"
                  style={{ userSelect: 'none' }}
                >
                  {currentFloorName}
                </span>
              ) : (
                <span
                  className="mx-auto rounded-sm animate-pulse bg-white/10"
                  style={{
                    height: matches ? 12 : 10,
                    width: '30%',
                    display: 'inline-block'
                  }}
                  aria-hidden
                />
              )}
            </div>

            {/* Image Name (no tooltip) */}
            <div
              className="text-ellipsis overflow-hidden"
              style={{
                fontSize: matches ? '24px' : '12px',
                fontWeight: 'bold',
                lineHeight: '1.3',
                maxHeight: '1.3em',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                display: '-webkit-box',
                wordBreak: 'break-word',
                marginTop: '2px',
                pointerEvents: 'auto'
              }}
            >
              {currentImageName ? (
                <span
                  className="block overflow-hidden text-ellipsis"
                  style={{ userSelect: 'none' }}
                >
                  {currentImageName}
                </span>
              ) : (
                <span
                  className="mx-auto rounded-sm animate-pulse bg-white/10"
                  style={{
                    height: matches ? 16 : 12,
                    width: '40%',
                    display: 'inline-block'
                  }}
                  aria-hidden
                />
              )}
            </div>
          </div>
        </div>

        <div
          className={`flex items-center flex-shrink-0 ${
            matches ? 'gap-x-1' : 'gap-x-2'
          }`}
        >
          <button
            type="button"
            onClick={() => setOpenBusinessInfo(true)}
            onMouseDown={stopPointerDown}
            onTouchStart={stopPointerDown}
            className={`carousel-action-btn cursor-pointer inline-flex items-center justify-center p-0 leading-none rounded-md transition active:opacity-80 flex-shrink-0 ${
              matches ? 'w-11 h-11' : 'w-10 h-10'
            }`}
            aria-label="open-business-info"
            title="Open business info"
          >
            <BusinessSettingIcon
              className={`${
                matches ? 'w-8 h-8' : 'w-6 h-6'
              } m-0 inline-block align-middle no-bg`}
            />
          </button>

          <button
            type="button"
            onClick={() => setOpenMapLocation(true)}
            onMouseDown={stopPointerDown}
            onTouchStart={stopPointerDown}
            className={`carousel-action-btn cursor-pointer inline-flex items-center justify-center p-0 leading-none rounded-md transition active:opacity-80 flex-shrink-0 ${
              matches ? 'w-11 h-11' : 'w-10 h-10'
            }`}
            aria-label="open-map-location"
            title="Open map location"
          >
            <SettingLocation
              className={`${
                matches ? 'w-8 h-8' : 'w-6 h-6'
              } m-0 inline-block align-middle no-bg`}
            />
          </button>

          <button
            type="button"
            onClick={toggleFullScreen}
            onMouseDown={stopPointerDown}
            onTouchStart={stopPointerDown}
            className={`carousel-action-btn cursor-pointer inline-flex items-center justify-center p-0 leading-none rounded-md transition active:opacity-80 flex-shrink-0 ${
              matches ? 'w-11 h-11' : 'w-10 h-10'
            }`}
            aria-label="toggle-fullscreen"
            title="Toggle fullscreen"
          >
            {isZoom ? (
              <ZoomIconOut
                className={`${
                  matches ? 'w-8 h-8' : 'w-6 h-6'
                } m-0 inline-block align-middle no-bg`}
              />
            ) : (
              <ZoomIcon
                className={`${
                  matches ? 'w-8 h-8' : 'w-6 h-6'
                } m-0 inline-block align-middle no-bg`}
              />
            )}
          </button>
        </div>
      </div>

      {/* Sub Image Carousel */}
      <SubImageThubnailCarousel
        subImageOverlay={subImageOverlay}
        handleClickSubImage={handleSelectSubImage}
        subImageCarousel={subImageCarousel}
        matches={matches}
        parentContainerRef={mainCarousel.containerRef}
      />

      {/* Carousel with background blur */}
      {isOpen && (
        <div className="bg-white/30 backdrop-blur-[2px] relative">
          <div
            className={`mx-auto opacity-100 relative ${
              matches ? 'max-h-[165px] w-[96%]' : 'max-h-[110px] w-full'
            }`}
          >
            <div
              ref={mainCarousel.containerRef}
              className={`flex overflow-x-auto no-scrollbar ${
                matches
                  ? 'h-[170px] space-x-6 px-8'
                  : 'h-[100px] space-x-2 px-3'
              } relative`}
              onMouseDown={mainCarousel.onMouseDown}
              onMouseMove={mainCarousel.onMouseMove}
              onMouseUp={mainCarousel.onMouseUp}
              onMouseLeave={mainCarousel.onMouseLeave}
              onTouchStart={mainCarousel.onTouchStart}
              onTouchMove={mainCarousel.onTouchMove}
              onTouchEnd={mainCarousel.onTouchEnd}
              onMouseEnter={mainCarousel.onMouseEnter}
              style={{
                cursor: mainCarousel.isDragging ? 'grabbing' : 'grab',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                willChange: 'scroll-position',
                contain: 'layout style paint',
                transform: 'translateZ(0)',
                backfaceVisibility: 'hidden'
              }}
            >
              {points
                .filter((p) => p.image360?.thumbnail)
                .sort((a, b) => a.order - b.order)
                .map((point) => {
                  const isSelected =
                    currentViewer.selectedImage === point.image360?.id

                  return (
                    <div
                      key={point.id}
                      className="flex flex-col justify-center items-center relative select-none"
                      ref={(el: HTMLDivElement | null) => {
                        thumbnailRefs.current[point.id] = el
                      }}
                    >
                      <div
                        onClick={(e) => {
                          e.stopPropagation() // Prevent parent motion.div or mainCarousel click logic
                          if (
                            subImageOverlay.isOpen &&
                            subImageOverlay.pointId === point.id
                          ) {
                            handleToggleSubImages(point.id)
                            return
                          }

                          if (currentViewer.selectedPoint !== point.id) {
                            handleThumbnailClick(point.id)
                          }

                          setTimeout(
                            () => {
                              handleToggleSubImages(point.id)
                            },
                            isSelected ? 0 : 500
                          )
                        }}
                        className={`absolute subimage-toggle-btn ${
                          isSelected ? '-top-[2px]' : '-top-0'
                        } z-10 ${
                          point.isSubImages ? 'block' : 'hidden'
                        } p-1 transition cursor-pointer`}
                      >
                        <ShowSubImageArrown
                          className={`absolute left-1/2 -translate-x-1/2 transition-all duration-300 ease-in-out transform ${
                            subImageOverlay.isOpen &&
                            subImageOverlay.pointId === point.id
                              ? 'rotate-180'
                              : 'rotate-0'
                          }
                          ${!matches ? 'scale-75' : 'scale-100'}`}
                        />
                      </div>

                      <motion.div
                        data-point-id={point.id}
                        initial={{ scale: 1.0 }}
                        animate={{ scale: isSelected ? 1.08 : 1.0 }}
                        transition={{ duration: 0.1 }}
                        style={{
                          zIndex: isSelected ? 1 : 0,
                          width: matches ? '168px' : '90px',
                          height: matches ? '117px' : '65px',
                          flexShrink: 0,
                          opacity: 1,
                          willChange: 'transform, opacity'
                        }}
                        onClick={() => {
                          if (currentViewer.imageIsLoading) return

                          handleThumbnailClick(point.id)
                          setCurrentViewer((prev) => ({
                            ...prev,
                            selectedImage: point.image360?.id,
                            isClickFromTourCarousel: true
                          }))
                        }}
                        className={`relative overflow-hidden transition-all duration-150 ${
                          isSelected ? 'selected-thumbnail' : 'border-0'
                        } ${
                          currentViewer.imageIsLoading
                            ? 'cursor-not-allowed opacity-50'
                            : 'cursor-pointer'
                        }`}
                      >
                        {point.isSubImages && (
                          <div className="absolute top-1 right-1 w-4 h-4 rounded-[2px] text-[10px] flex bg-white text-black items-center justify-center z-10 select-none">
                            {point.numberOfSubImages ||
                              (point.subImages ? point.subImages.length : 0)}
                          </div>
                        )}
                        <LazyThumbnail
                          imageType={ImageType.OBJECT_FILL}
                          url={
                            point.image360?.multires
                              ? `${point.image360?.tilesUrl}thumb.jpg`
                              : point.image360?.thumbnail || ''
                          }
                          className="w-full h-full"
                          name={point.image360?.name || ''}
                          width={168}
                          height={117}
                        />
                        {matches ? (
                          <Tooltip
                            title={point.image360?.name}
                            placement="top"
                            color="#0E0D0A80"
                            trigger="hover"
                          >
                            <div
                              className="absolute inset-0 select-none px-2 text-white text-[13px] font-bold text-center p-2 flex flex-col justify-end pointer-events-none"
                              style={{
                                width: '100%',
                                lineHeight: '1.3',
                                overflowWrap: 'break-word',
                                whiteSpace: 'normal'
                              }}
                            >
                              <span
                                className="block pointer-events-auto"
                                style={{
                                  maxHeight: matches ? '6.5em' : '5.2em',
                                  overflow: 'hidden'
                                }}
                              >
                                {point.image360?.name}
                              </span>
                            </div>
                          </Tooltip>
                        ) : (
                          <div
                            className="absolute inset-0 select-none px-1 text-white text-[10px] font-bold text-center p-1 flex flex-col justify-end pointer-events-none"
                            style={{
                              width: '100%',
                              lineHeight: '1.3',
                              overflowWrap: 'break-word',
                              whiteSpace: 'normal'
                            }}
                            aria-hidden
                          >
                            <span
                              className="block pointer-events-auto"
                              style={{
                                maxHeight: matches ? '6.5em' : '5.2em',
                                overflow: 'hidden'
                              }}
                            >
                              {point.image360?.name}
                            </span>
                          </div>
                        )}
                      </motion.div>
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ThumbnailCarousel
